import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import html2canvas from "html2canvas";
import { RgbaStringColorPicker, HexColorPicker, HexAlphaColorPicker } from "react-colorful";
import "./ColorPicker.css";

const RGBA_RE = /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+(\s*,\s*(0|1|0?\.\d+))?\s*\)$/i;
const HEX_RE = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

const isValid = (val) => RGBA_RE.test(val?.trim()) || HEX_RE.test(val?.trim());

// Expands #rgb/#rgba shorthand and reads the alpha byte out of #rrggbb(aa).
const hexToRgba = (hex) => {
  const h = hex.replace("#", "");
  const full = h.length === 3 || h.length === 4
    ? h.split("").map((c) => c + c).join("")
    : h;
  const n = parseInt(full.slice(0, 6), 16);
  const a = full.length === 8 ? parseInt(full.slice(6, 8), 16) / 255 : 1;
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${Math.round(a * 100) / 100})`;
};
// Only appends the alpha byte when the colour isn't fully opaque, matching
// react-colorful's own hex-alpha serialization convention.
const rgbaToHex = (rgba) => {
  const m = rgba.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*(0|1|0?\.\d+))?\s*\)/i);
  if (!m) return "#ffffff";
  const hex = [m[1], m[2], m[3]].map((v) => parseInt(v).toString(16).padStart(2, "0")).join("");
  const alpha = m[4] === undefined ? 1 : parseFloat(m[4]);
  if (alpha >= 1) return `#${hex}`;
  return `#${hex}${Math.round(alpha * 255).toString(16).padStart(2, "0")}`;
};

// Live numeric readout for whatever's currently in the text/picker value —
// the react-colorful sliders are drag-only with no numbers of their own.
const parseReadout = (value, mode) => {
  if (mode === "rgba") {
    const m = value?.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*(0|1|0?\.\d+))?\s*\)/i);
    if (!m) return null;
    const alpha = m[4] === undefined ? 1 : parseFloat(m[4]);
    return { r: m[1], g: m[2], b: m[3], alphaPct: Math.round(alpha * 100) };
  }
  const h = value?.replace("#", "") || "";
  const full = h.length === 3 || h.length === 4
    ? h.split("").map((c) => c + c).join("")
    : h;
  if (![6, 8].includes(full.length)) return null;
  const alpha = full.length === 8 ? parseInt(full.slice(6, 8), 16) / 255 : 1;
  return { hex: `#${full.slice(0, 6)}`, alphaPct: Math.round(alpha * 100) };
};

const POPUP_WIDTH = 220;
const MAX_BRAND_COLORS = 10;

const hasNativeEyeDropper = typeof window !== "undefined" && "EyeDropper" in window;

const waitForPaint = () => new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

const rgbToHex = (r, g, b) =>
  `#${[r, g, b]
    .map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0"))
    .join("")}`;

// Sample a pixel colour from a pre-captured full-page snapshot (see
// captureScreenForSampling). Needed because Safari/Firefox have no native
// EyeDropper API — reading live <canvas> elements alone misses colours on
// regular DOM nodes (hotspot backgrounds, images, etc.), which is why that
// approach always returned black.
const sampleCapturedColor = (captured, clientX, clientY) => {
  if (!captured) return null;
  const { canvas, scrollX, scrollY } = captured;
  try {
    const px = Math.floor(clientX + scrollX);
    const py = Math.floor(clientY + scrollY);
    if (px < 0 || py < 0 || px >= canvas.width || py >= canvas.height) return null;
    const [r, g, b] = canvas.getContext("2d").getImageData(px, py, 1, 1).data;
    return rgbToHex(r, g, b);
  } catch {
    return null;
  }
};

const canvasIsReadable = (canvas) => {
  try {
    canvas.getContext("2d").getImageData(0, 0, 1, 1);
    return true;
  } catch {
    return false;
  }
};

const opaqueBgColor = (node) => {
  const bg = getComputedStyle(node).backgroundColor;
  const m = bg && bg.match(/^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:[,\s/]+([\d.]+))?/i);
  if (!m || (m[4] !== undefined && parseFloat(m[4]) <= 0)) return null;
  return rgbToHex(parseFloat(m[1]), parseFloat(m[2]), parseFloat(m[3]));
};

const pixelFromNode = (node, clientX, clientY, naturalW, naturalH) => {
  try {
    const rect = node.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const scratch = document.createElement("canvas");
    scratch.width = naturalW;
    scratch.height = naturalH;
    const ctx = scratch.getContext("2d");
    if (node.tagName === "IMG") ctx.drawImage(node, 0, 0, naturalW, naturalH);
    else ctx.drawImage(node, 0, 0);
    const sx = Math.floor((clientX - rect.left) * (naturalW / rect.width));
    const sy = Math.floor((clientY - rect.top) * (naturalH / rect.height));
    const [r, g, b, a] = ctx.getImageData(sx, sy, 1, 1).data;
    return a === 0 ? null : rgbToHex(r, g, b);
  } catch {
    return null;
  }
};

const sampleDomColor = (clientX, clientY) => {
  if (typeof document === "undefined" || !document.elementsFromPoint) return null;
  const stack = document
    .elementsFromPoint(clientX, clientY)
    .filter((n) => !n.closest(".kz-eyedropper-overlay"));
  if (!stack.length) return null;

  const topBg = opaqueBgColor(stack[0]);
  if (topBg) return topBg;

  for (const node of stack) {
    if (node.tagName === "CANVAS" && node.width && node.height) {
      const hit = pixelFromNode(node, clientX, clientY, node.width, node.height);
      if (hit) return hit;
    }
    if (node.tagName === "IMG" && (node.naturalWidth || node.width)) {
      const hit = pixelFromNode(node, clientX, clientY, node.naturalWidth || node.width, node.naturalHeight || node.height);
      if (hit) return hit;
    }
  }

  for (let node = stack[0]; node && node.nodeType === 1; node = node.parentElement) {
    const bg = opaqueBgColor(node);
    if (bg) return bg;
  }
  return "#ffffff";
};

const captureScreenForSampling = async () => {
  const canvas = await html2canvas(document.body, {
    useCORS: true,
    allowTaint: false,
    scale: 1,
    logging: false,
  });
  return { canvas, scrollX: window.scrollX, scrollY: window.scrollY };
};

const PickerPopup = ({ rect, onClose, anchorEl, children }) => {
  const popupRef = useRef(null);
  const [style, setStyle] = useState(() => ({
    position: "fixed",
    left: Math.min(rect.left, window.innerWidth - POPUP_WIDTH - 8),
    top: rect.bottom + 4,
    zIndex: 99999,
    visibility: "hidden",
  }));

  useLayoutEffect(() => {
    const el = popupRef.current;
    if (!el) return;

    const reposition = () => {
      const height = el.offsetHeight;
      const left = Math.min(rect.left, window.innerWidth - POPUP_WIDTH - 8);
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      let top = spaceBelow >= height + 8 || spaceBelow >= spaceAbove
        ? rect.bottom + 4
        : rect.top - height - 4;
      top = Math.max(8, Math.min(top, window.innerHeight - height - 8));
      setStyle({ position: "fixed", left, top, zIndex: 99999, visibility: "visible" });
    };

    reposition();
    const observer = new ResizeObserver(reposition);
    observer.observe(el);
    return () => observer.disconnect();
  }, [rect]);

  useEffect(() => {
    const handle = (e) => {
      if (anchorEl?.contains(e.target)) return;
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [onClose, anchorEl]);

  return ReactDOM.createPortal(
    <div className="kz-color-picker__popup" style={style} ref={popupRef}>
      {children}
    </div>,
    document.body
  );
};

// Canvas-sampling overlay for Safari/Firefox (no native EyeDropper API).
// Samples from a pre-captured full-page snapshot rather than live <canvas>
// elements, so it can pick up colours from any rendered DOM content.
const CanvasSamplerOverlay = ({ captured, onPick, onCancel }) => {
  const [preview, setPreview] = useState(null); // { x, y, color }

  const sample = useCallback((x, y) => {
    return sampleCapturedColor(captured, x, y) ?? sampleDomColor(x, y);
  }, [captured]);

  const handleMove = useCallback((e) => {
    const color = sample(e.clientX, e.clientY);
    setPreview(color ? { x: e.clientX, y: e.clientY, color } : null);
  }, [sample]);

  const handleClick = useCallback((e) => {
    const color = sample(e.clientX, e.clientY);
    if (color) onPick(color);
    else onCancel();
  }, [sample, onPick, onCancel]);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") onCancel(); };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onCancel]);

  return ReactDOM.createPortal(
    <div
      className="kz-eyedropper-overlay"
      onMouseMove={handleMove}
      onClick={handleClick}
    >
      <div className="kz-eyedropper-hint">
        <i className="fa-solid fa-eye-dropper me-2" />
        Click on the document to pick a colour &nbsp;·&nbsp; <kbd>Esc</kbd> to cancel
      </div>
      {preview && (
        <div
          className="kz-eyedropper-preview"
          style={{ left: preview.x + 18, top: preview.y + 18 }}
        >
          <div className="kz-eyedropper-preview__swatch" style={{ background: preview.color }} />
          <span>{preview.color}</span>
        </div>
      )}
    </div>,
    document.body
  );
};

const ColorPicker = ({
  color,
  onChange,
  onClose,
  name,
  alpha = true,
  label,
  className = "",
  swatchOnly = false,
  brandColors = [],
  onAddBrandColor,
  onRemoveBrandColor,
  anchorEl,
  open: openProp,
  onOpenChange,
}) => {
  const controlled = anchorEl !== undefined;
  const safeColor = color || "rgba(255,255,255,1)";
  const [openState, setOpenState] = useState(false);
  const open = controlled ? !!openProp : openState;
  const setOpen = useCallback((next) => {
    if (!controlled) {
      setOpenState(next);
      return;
    }
    onOpenChange?.(typeof next === "function" ? next(!!openProp) : next);
  }, [controlled, onOpenChange, openProp]);
  const [anchorRect, setAnchorRect] = useState(null);
  const [text, setText]           = useState(safeColor);
  const [textValid, setTextValid] = useState(true);
  const [mode, setMode]           = useState("hex");
  const [sampling, setSampling]   = useState(false);
  const [captured, setCaptured]   = useState(null);
  const swatchRef = useRef(null);
  const modalChromeRef = useRef({ modalEl: null, hadBackground: false });
  const wasOpenRef = useRef(false);
  const onCloseRef = useRef(onClose);
  const lastValidColorRef = useRef(isValid(safeColor) ? safeColor : "#ffffff");
  onCloseRef.current = onClose;

  useEffect(() => {
    if (mode === "hex" && RGBA_RE.test(safeColor?.trim())) {
      setText(rgbaToHex(safeColor));
    } else {
      setText(safeColor);
    }
    setTextValid(true);
  }, [safeColor]);

  useEffect(() => {
    if (isValid(text)) lastValidColorRef.current = text;
  }, [text]);

  useEffect(() => {
    if (wasOpenRef.current && !open) onCloseRef.current?.();
    wasOpenRef.current = open;
  }, [open]);

  useLayoutEffect(() => {
    if (controlled && open && anchorEl) setAnchorRect(anchorEl.getBoundingClientRect());
  }, [controlled, open, anchorEl]);

  const anchorNode = () => (controlled ? anchorEl : swatchRef.current);

  const emit = useCallback(
    (value) => {
      if (!onChange) return;
      onChange(name ? { target: { name, value } } : value);
    },
    [onChange, name]
  );

  const applyColor = useCallback((hex) => {
    const value = mode === "rgba" ? hexToRgba(hex) : hex;
    setText(value);
    setTextValid(true);
    emit(value);
  }, [mode, emit]);

  const handleSwatchClick = () => {
    if (!open && swatchRef.current) {
      setAnchorRect(swatchRef.current.getBoundingClientRect());
    }
    setOpen((o) => !o);
  };

  const handleBrandSwatchClick = (hex) => {
    applyColor(hex);
    setOpen(false);
  };

  const handleSaveToBrand = () => {
    if (!onAddBrandColor || !textValid || !text) return;
    onAddBrandColor(text);
  };

  const handleRemoveBrandColor = (hex) => {
    onRemoveBrandColor?.(hex);
  };

  const handlePickerChange = (value) => {
    setText(value);
    setTextValid(true);
    emit(value);
  };

  const handleReadoutKeyDown = (e) => {
    if (e.key !== "Enter") return;
    if (textValid && text) setOpen(false);
  };

  const handleTextChange = (e) => {
    const value = e.target.value.trim();
    setText(value);
    if (value === "" || value === "transparent") {
      setTextValid(true);
      emit(value);
      return;
    }
    if (!alpha && !value.startsWith("#")) {
      const normalized = `#${value}`;
      if (HEX_RE.test(normalized)) {
        setTextValid(true);
        emit(normalized);
        return;
      }
    }
    if (isValid(value)) {
      setTextValid(true);
      emit(value);
    } else {
      setTextValid(false);
    }
  };

  const handleModeToggle = () => {
    setMode((prev) => {
      const next = prev === "rgba" ? "hex" : "rgba";
      if (textValid && text) {
        const converted = next === "hex" ? rgbaToHex(text) : hexToRgba(text);
        setText(converted);
        emit(converted);
      }
      return next;
    });
  };

  // The rest of the page dims to 60% black (body.Background, see
  // app/assets/stylesheets/modal.css) whenever a modal is open, which makes
  // it impossible to pick a true colour from anything behind it. Hide the
  // modal and that overlay while sampling, then restore both afterwards.
  const hideModalChrome = () => {
    const modalEl = anchorNode()?.closest(".elementModal");
    modalChromeRef.current.modalEl = modalEl || null;
    if (modalEl) modalEl.style.visibility = "hidden";
    if (document.body.classList.contains("Background")) {
      document.body.classList.remove("Background");
      modalChromeRef.current.hadBackground = true;
    } else {
      modalChromeRef.current.hadBackground = false;
    }
  };

  const showModalChrome = () => {
    const { modalEl, hadBackground } = modalChromeRef.current;
    if (modalEl) modalEl.style.visibility = "";
    if (hadBackground) document.body.classList.add("Background");
    modalChromeRef.current = { modalEl: null, hadBackground: false };
  };

  const reopenPopup = () => {
    if (anchorNode()) setAnchorRect(anchorNode().getBoundingClientRect());
    setOpen(true);
  };

  const handleNativeEyeDropper = async () => {
    setOpen(false);
    hideModalChrome();
    await waitForPaint();
    try {
      const dropper = new window.EyeDropper();
      const result = await dropper.open();
      applyColor(result.sRGBHex);
    } catch {
      // user cancelled
    } finally {
      showModalChrome();
      reopenPopup();
    }
  };

  const handleCanvasEyeDropper = async () => {
    setOpen(false);
    hideModalChrome();
    await waitForPaint();
    let snapshot = null;
    try {
      snapshot = await captureScreenForSampling();
    } catch {
      snapshot = null;
    }
    setCaptured(snapshot && canvasIsReadable(snapshot.canvas) ? snapshot : null);
    setSampling(true);
  };

  const endSampling = (hex) => {
    if (hex) applyColor(hex);
    setSampling(false);
    setCaptured(null);
    showModalChrome();
    reopenPopup();
  };

  const swatchColor = textValid && text ? text : "#ffffff";
  const PickerComponent = mode === "rgba"
    ? RgbaStringColorPicker
    : (alpha ? HexAlphaColorPicker : HexColorPicker);
  const readout = parseReadout(text, mode);
  const pickerColor = isValid(text) ? text : lastValidColorRef.current;

  return (
    <>
      <div className={`kz-color-picker${swatchOnly ? " kz-color-picker--swatch-only" : ""} ${className}`}>
        {!controlled && label && <span className="kz-color-picker__label">{label}</span>}
        {!controlled && <div className="kz-color-picker__row">
          <button
            type="button"
            ref={swatchRef}
            className="kz-color-picker__swatch"
            style={{ "--kz-swatch-color": swatchColor }}
            onClick={handleSwatchClick}
            aria-label="Open colour picker"
          />
          {!swatchOnly && (
            <input
              type="text"
              className={`kz-color-picker__text${textValid ? "" : " kz-color-picker__text--invalid"}`}
              value={text}
              onChange={handleTextChange}
              spellCheck={false}
            />
          )}
        </div>}
        {open && anchorRect && (
          <PickerPopup rect={anchorRect} onClose={() => setOpen(false)} anchorEl={controlled ? anchorEl : undefined}>
            <div className="kz-color-picker__mode-toggle">
              <button
                type="button"
                className={`kz-color-picker__mode-btn${mode === "hex" ? " kz-color-picker__mode-btn--active" : ""}`}
                onClick={handleModeToggle}
                disabled={mode === "hex"}
              >
                HEX
              </button>
              <button
                type="button"
                className={`kz-color-picker__mode-btn${mode === "rgba" ? " kz-color-picker__mode-btn--active" : ""}`}
                onClick={handleModeToggle}
                disabled={mode === "rgba"}
              >
                RGB
              </button>
              <button
                type="button"
                className="kz-color-picker__eyedropper-btn"
                onClick={hasNativeEyeDropper ? handleNativeEyeDropper : handleCanvasEyeDropper}
                title={hasNativeEyeDropper ? "Pick colour from screen" : "Pick colour from document"}
                aria-label="Eyedropper"
              >
                <i className="fa-solid fa-eye-dropper" />
              </button>
            </div>
            <PickerComponent color={pickerColor} onChange={handlePickerChange} />
            {mode === "hex" ? (
              <div className="kz-color-picker__readout">
                <input
                  type="text"
                  className={`kz-color-picker__readout-hex-input${textValid ? "" : " kz-color-picker__text--invalid"}`}
                  value={text}
                  onChange={handleTextChange}
                  onKeyDown={handleReadoutKeyDown}
                  spellCheck={false}
                />
                {alpha && readout && (
                  <span className="kz-color-picker__readout-alpha">
                    <b>Opacity</b> {readout.alphaPct}%
                  </span>
                )}
              </div>
            ) : (
              readout && (
                <div className="kz-color-picker__readout">
                  <span><b>R</b> {readout.r}</span>
                  <span><b>G</b> {readout.g}</span>
                  <span><b>B</b> {readout.b}</span>
                  {alpha && (
                    <span className="kz-color-picker__readout-alpha">
                      <b>Opacity</b> {readout.alphaPct}%
                    </span>
                  )}
                </div>
              )
            )}
            {(brandColors.length > 0 || onAddBrandColor) && (
              <div className="kz-color-picker__brandRow">
                <span className="kz-color-picker__brandLabel">Brand colors</span>
                <div className="kz-color-picker__brandSwatches">
                  {brandColors.map((brandColor) => (
                    <span key={brandColor} className="kz-color-picker__brandSwatchWrap">
                      <button
                        type="button"
                        className="kz-color-picker__brandSwatch"
                        style={{ background: brandColor }}
                        onClick={() => handleBrandSwatchClick(brandColor)}
                        aria-label={`Use brand color ${brandColor}`}
                        title={brandColor}
                      />
                      {onRemoveBrandColor && (
                        <button
                          type="button"
                          className="kz-color-picker__brandSwatchRemove"
                          onClick={(e) => { e.stopPropagation(); handleRemoveBrandColor(brandColor); }}
                          aria-label={`Remove brand color ${brandColor}`}
                        >
                          ✕
                        </button>
                      )}
                    </span>
                  ))}
                  {onAddBrandColor && brandColors.length < MAX_BRAND_COLORS && (
                    <button
                      type="button"
                      className="kz-color-picker__brandSwatch kz-color-picker__brandAdd"
                      onClick={handleSaveToBrand}
                      disabled={!textValid || !text}
                      aria-label="Save current color to brand colors"
                      title="Save current color to brand colors"
                    >
                      +
                    </button>
                  )}
                </div>
              </div>
            )}
          </PickerPopup>
        )}
      </div>
      {sampling && (
        <CanvasSamplerOverlay
          captured={captured}
          onPick={(hex) => endSampling(hex)}
          onCancel={() => endSampling(null)}
        />
      )}
    </>
  );
};

export default ColorPicker;
